import React, { useState, useEffect, useContext} from 'react';
import { useNavigate,useLocation } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import api from '../services/axios';
import alertService from '../services/alerts';
import '../styles/CursosPage.css';
import { AuthContext } from '../context/AuthContext';

function CursosPage() {
  const [courses, setCourses] = useState([]);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const navigate = useNavigate();
  const location = useLocation(); 
  const itemsPerPage = 5;
  const fetchCursos = async () => {
    try {
      const response = await api.get('/cursos');
      setCourses(response.data);
      setError('');
    } catch (err) {
      if (err.response && err.response.status === 403) {
        setError('No tienes permisos para ver los cursos');
      } else {
        setError('Error al obtener la lista de cursos');
      }
    }
  };
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1025);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 1025;
      setIsMobile(mobile);
      if (!mobile) {
        setSidebarOpen(true);
      } else {
        setSidebarOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    if (window.innerWidth >= 1025) {
      setSidebarOpen(true);
    }
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (isMobile) {
      setSidebarOpen(false);
    }
  }, [location, isMobile]);

  const toggleSidebar = () => {
    setSidebarOpen((prev) => !prev);
  };

  const handleCloseSidebar = () => {
    if (isMobile && sidebarOpen) {
      setSidebarOpen(false);
    }
  };

  useEffect(() => {
    fetchCursos();
  }, []);

  const handleSearch = (event) => {
    setSearchQuery(event.target.value);
    setCurrentPage(1); 
  };

  const filteredCourses = courses.filter(course =>
    course.nombre_curso.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const coursesToShow = searchQuery.trim() === ''
    ? filteredCourses.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
    : filteredCourses;

  const handleEdit = (id) => {
    navigate(`/cursos/editar/${id}`);
  };

  const handleDelete = async (id) => {
    const confirmed = await alertService.confirm(
      'Eliminar Curso',
      '¿Estás seguro de que deseas eliminar este curso? Esta acción no se puede deshacer.',
      {
        confirmButtonText: 'Eliminar',
        cancelButtonText: 'Cancelar'
      }
    );

    if (!confirmed.isConfirmed) return;

    try {
      await api.delete(`/cursos/${id}`);
      setCourses(courses.filter(course => course.id_curso !== id));
      await alertService.success(
        'Curso Eliminado',
        'El curso ha sido eliminado exitosamente.'
      );
    } catch (err) {
      let errorMessage = 'Error al eliminar el curso';
      
      if (err.response && err.response.status === 409) {
        if (err.response.data.details) {
          const details = err.response.data.details;
          errorMessage = `${err.response.data.error}\n\n${details.message}\n\nCurso: ${details.curso}\nProspectos: ${details.prospectos}\nAlumnos: ${details.alumnos}`;
        } else {
          errorMessage = err.response.data.error || 'No se puede eliminar el curso porque está siendo utilizado en otra tabla.';
        }
      } else if (err.response && err.response.status === 403) {
        errorMessage = 'No tienes permisos para eliminar cursos.';
      }
      
      await alertService.error('Error', errorMessage);
    }
  };

  const nextPage = () => {
    if (currentPage * itemsPerPage < filteredCourses.length) {
      setCurrentPage(currentPage + 1);
    }
  };

  const prevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const { user } = useContext(AuthContext); 
  const allowedRolesForActions = ['administrador', 'moderador']; 
  const DRolesForActions = ['administrador'];

  return (
    <div className="dashboard-container">
      {(!isMobile || (isMobile && sidebarOpen)) && (
        <Sidebar 
          onLinkClick={handleCloseSidebar} 
        />
      )}
  
      <div className="main-content" onClick={handleCloseSidebar}>
        <div className="dashboard-title-card">
          <button className="menu-toggle" onClick={toggleSidebar}>
            <i className="fas fa-bars"></i>
          </button>
          <div className="dashboard-title-section">
            <h1 className="dashboard-title">Cursos</h1>
            <span className="dashboard-title-sub">Gestiona todos los cursos disponibles</span>
          </div>
        </div>

        {error && (
          <div className="error-message">
            <p>{error}</p>
          </div>
        )}
  
        <div className="search-filter-container">
          <input
            type="text"
            placeholder="Buscar por nombre del curso..."
            value={searchQuery}
            onChange={handleSearch}
            className="search-input"
          />
        </div>
  
        <div className="add-button-container">
          {allowedRolesForActions.includes(user?.rol) && (
            <button 
              className="add-button" 
              onClick={() => navigate('/cursos/agregar')}
              title="Agregar nuevo curso"
            >
              <i className="fas fa-plus"></i>
              Agregar Curso
            </button>
          )}
        </div>
  
        <div className="futuristic-table-wrapper">
          <div className="table-header-futuristic">
            <div className="header-stats">
              <div className="stat-item">
                <span className="stat-label">Total Cursos</span>
                <span className="stat-value">{courses.length}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Mostrando</span>
                <span className="stat-value">{coursesToShow.length}</span>
              </div>
            </div>
          </div>
          
          <div className="futuristic-table">
            {coursesToShow.length > 0 ? (
              coursesToShow.map((course) => (
                <div key={course.id_curso} className="table-row-futuristic">
                  <div className="row-header">
                    <div className="curso-id">
                      <span className="id-label">ID</span>
                      <span className="id-value">{course.id_curso}</span>
                    </div>
                    <div className="curso-name">
                      <span className="name-text">{course.nombre_curso}</span>
                    </div>
                    {allowedRolesForActions.includes(user?.rol) && (
                      <div className="action-buttons">
                        <button 
                          className="action-btn edit-btn" 
                          onClick={() => handleEdit(course.id_curso)}
                          title="Editar curso"
                        >
                          <i className="fas fa-edit"></i>
                        </button>
                        {DRolesForActions.includes(user?.rol) && (
                          <button 
                            className="action-btn delete-btn" 
                            onClick={() => handleDelete(course.id_curso)}
                            title="Eliminar curso"
                          >
                            <i className="fas fa-trash"></i>
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                  
                  <div className="row-content">
                    <div className="info-grid">
                      <div className="info-item">
                        <span className="info-label">Nombre del Curso</span>
                        <span className="info-value">{course.nombre_curso}</span>
                      </div>
                      <div className="info-item">
                        <span className="info-label">Código del Curso</span>
                        <span className="code-badge">{course.codigo_curso}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="empty-state">
                <div className="empty-icon">
                  <i className="fas fa-graduation-cap"></i>
                </div>
                <h3>No hay cursos disponibles</h3>
                <p>No se encontraron cursos que coincidan con tu búsqueda.</p>
              </div>
            )}
          </div>
        </div>
  
        {searchQuery.trim() === '' && (
          <div className="pagination-container">
            {currentPage > 1 && (
              <button onClick={prevPage}>
                <i className="fas fa-chevron-left"></i>
                Anterior
              </button>
            )}
            <span>
              Página {currentPage} de {Math.ceil(filteredCourses.length / itemsPerPage)}
            </span>
            {currentPage * itemsPerPage < filteredCourses.length && (
              <button onClick={nextPage}>
                Siguiente
                <i className="fas fa-chevron-right"></i>
              </button>
            )}
          </div>
        )}
      </div>
  
      {isMobile && sidebarOpen && (
        <div className="overlay" onClick={handleCloseSidebar} />
      )}
    </div>
  );
}

export default CursosPage;
